import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiCall, PAY_METHODS, Sel, euro } from "./rUtils";
import { DateField } from "../calendar/DateField";
import { LinesEditor, toLineIn } from "./LinesEditor";
import { todayBerlin } from "../calendar/calendarUtils";

const useRun = (onDone) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return [busy, run];
};

export function PaymentForm({ token, inv, onDone }) {
  const [f, setF] = useState({ amount: inv.open_amount, date: todayBerlin(), method: "bank_transfer", note: "" });
  const [busy, run] = useRun(onDone);
  const submit = () => run(() => apiCall(token, `/rechnungen/admin/invoice/${inv.invoice_id}/payments`, { method: "POST", body: f }), "Zahlung erfasst");
  return (
    <div className="border border-white/10 p-3 space-y-3" data-testid="payment-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-[#A1A1AA]">Betrag (offen: {euro(inv.open_amount)})</Label><Input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="payment-amount" /></div>
        <div><Label className="text-xs text-[#A1A1AA]">Zahlungsdatum</Label><DateField value={f.date} onChange={(v) => setF({ ...f, date: v })} testId="payment-date" /></div>
        <div><Label className="text-xs text-[#A1A1AA]">Zahlungsart</Label><Sel value={f.method} onChange={(v) => setF({ ...f, method: v })} options={PAY_METHODS} testId="payment-method" className="w-full h-10" /></div>
        <div><Label className="text-xs text-[#A1A1AA]">Notiz / Nachweis</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="z. B. Kontoauszug 10/2026" className="bg-[#0A0A0A] border-white/20 text-white" data-testid="payment-note" /></div>
      </div>
      <div className="flex gap-2">
        <Button disabled={busy} onClick={submit} className="bg-green-600 hover:bg-green-700 text-white rounded-none" data-testid="payment-submit">Zahlung erfassen</Button>
        <Button disabled={busy} variant="outline" onClick={() => setF({ ...f, amount: inv.open_amount })} className="border-white/20 text-white rounded-none" data-testid="payment-full">Restbetrag übernehmen</Button>
      </div>
    </div>
  );
}

export function StornoForm({ token, inv, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, run] = useRun(onDone);
  return (
    <div className="border border-red-500/30 p-3 space-y-2" data-testid="storno-form">
      <p className="text-xs text-[#A1A1AA]">Es wird eine Stornorechnung mit eigener Nummer erzeugt und mit dieser Rechnung verknüpft. Die Original-PDF bleibt erhalten.</p>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Grund der Stornierung" rows={2} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="storno-reason" />
      <Button disabled={busy || reason.trim().length < 3} onClick={() => window.confirm("Rechnung wirklich stornieren?") && run(() => apiCall(token, `/rechnungen/admin/invoice/${inv.invoice_id}/storno`, { method: "POST", body: { reason } }), "Stornorechnung erstellt")} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="storno-submit">Stornieren</Button>
    </div>
  );
}

export function CorrectionForm({ token, inv, lines: initial, onDone }) {
  const [f, setF] = useState({ reason: "", customer_name: inv.customer_name, customer_address: inv.customer_address, service_date: "" });
  const [lines, setLines] = useState(initial.map((l) => ({ description: l.description, quantity: l.quantity || 1, unit_price: l.unit_price })));
  const [busy, run] = useRun(onDone);
  const submit = () => run(() => apiCall(token, `/rechnungen/admin/invoice/${inv.invoice_id}/korrektur`, { method: "POST", body: { ...f, line_items: toLineIn(lines) } }), "Korrektur erstellt (Storno + neue Rechnung)");
  return (
    <div className="border border-purple-500/30 p-3 space-y-3" data-testid="correction-form">
      <p className="text-xs text-[#A1A1AA]">Die Originalrechnung wird storniert und durch eine neue Rechnung mit neuer Nummer ersetzt. Alle Belege bleiben verknüpft.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-[#A1A1AA]">Kunde</Label><Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="correction-name" /></div>
        <div><Label className="text-xs text-[#A1A1AA]">Leistungsdatum (leer = unverändert)</Label><Input value={f.service_date} onChange={(e) => setF({ ...f, service_date: e.target.value })} placeholder="TT.MM.JJJJ" className="bg-[#0A0A0A] border-white/20 text-white" data-testid="correction-service-date" /></div>
        <div className="col-span-2"><Label className="text-xs text-[#A1A1AA]">Rechnungsanschrift</Label><Input value={f.customer_address} onChange={(e) => setF({ ...f, customer_address: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="correction-address" /></div>
      </div>
      <LinesEditor lines={lines} onChange={setLines} testId="correction-lines" />
      <Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Grund der Korrektur" rows={2} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="correction-reason" />
      <Button disabled={busy || f.reason.trim().length < 3} onClick={submit} className="bg-purple-600 hover:bg-purple-700 text-white rounded-none" data-testid="correction-submit">Korrektur ausstellen</Button>
    </div>
  );
}

export function NoteForm({ token, inv, onDone }) {
  const [note, setNote] = useState("");
  const [busy, run] = useRun(() => { setNote(""); onDone(); });
  return (
    <div className="flex gap-2" data-testid="note-form">
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Interne Notiz hinzufügen" className="bg-[#0A0A0A] border-white/20 text-white" data-testid="note-input" />
      <Button disabled={busy || !note.trim()} onClick={() => run(() => apiCall(token, `/rechnungen/admin/invoice/${inv.invoice_id}/notes`, { method: "POST", body: { note } }), "Notiz gespeichert")} variant="outline" className="border-white/20 text-white rounded-none" data-testid="note-submit">Speichern</Button>
    </div>
  );
}
